import React, { useState } from "react";

import {
  Button,
  ButtonGroup,
  FormControl,
  FormHelperText,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

import { TierPreset } from "./k8sResources";

// ── Types ──────────────────────────────────────────────────────────────────────

interface ResourceTierButtonsProps {
  /** Display label (e.g., "CPU", "Memory", "Storage") */
  label: string;
  /** Current value as K8s quantity string (e.g., "500m", "512Mi", "10Gi") */
  value: string;
  /** Called when the value changes */
  onChange: (value: string) => void;
  /** Preset tiers for this resource */
  tiers: TierPreset[];
  /** Suffix for custom input (e.g., "m", "Mi", "Gi") */
  customSuffix: string;
  /** Whether the control is disabled */
  disabled?: boolean;
  /** Helper text below the buttons */
  helperText?: string;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function ResourceTierButtons({
  label,
  value,
  onChange,
  tiers,
  customSuffix,
  disabled = false,
  helperText,
}: ResourceTierButtonsProps): React.ReactElement {
  const [isCustom, setIsCustom] = useState(false);
  const [customInput, setCustomInput] = useState("");

  // Determine which tier is active (or if custom)
  const activeTier = tiers.find((t) => t.value === value);
  const activeLabel = activeTier ? activeTier.label : "Custom";

  // If value matches a tier, ensure custom mode is off
  React.useEffect(() => {
    if (activeTier) {
      setIsCustom(false);
    }
  }, [activeTier]);

  const handleTierClick = (tier: TierPreset) => {
    setIsCustom(false);
    onChange(tier.value);
  };

  const handleCustomClick = () => {
    setIsCustom(true);
    // Pre-fill the current value into the custom input
    setCustomInput(value.replace(customSuffix, ""));
  };

  const handleCustomChange = (input: string) => {
    setCustomInput(input);
    const numVal = parseFloat(input);
    if (!isNaN(numVal) && numVal > 0) {
      onChange(`${input}${customSuffix}`);
    }
  };

  const handleCustomBlur = () => {
    // If custom input is empty or invalid, revert to the current value
    const numVal = parseFloat(customInput);
    if (isNaN(numVal) || numVal <= 0) {
      setIsCustom(false);
    }
  };

  return (
    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>

      <ButtonGroup
        size="small"
        variant="outlined"
        disabled={disabled}
        sx={{ flexWrap: "wrap", gap: 0.5, mb: isCustom ? 1 : 0 }}
      >
        {tiers.map((tier) => (
          <Button
            key={tier.value}
            variant={activeLabel === tier.label ? "contained" : "outlined"}
            onClick={() => handleTierClick(tier)}
            sx={{
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
              textTransform: "none",
            }}
          >
            {tier.label}
          </Button>
        ))}
        <Button
          variant={activeLabel === "Custom" ? "contained" : "outlined"}
          onClick={handleCustomClick}
          sx={{
            minWidth: "auto",
            px: 1.5,
            py: 0.5,
            fontSize: "0.75rem",
            textTransform: "none",
          }}
        >
          Custom
        </Button>
      </ButtonGroup>

      {isCustom && (
        <TextField
          size="small"
          type="number"
          value={customInput}
          onChange={(e) => handleCustomChange(e.target.value)}
          onBlur={handleCustomBlur}
          disabled={disabled}
          inputProps={{
            min: 0,
            step: customSuffix === "m" ? 50 : customSuffix === "Mi" ? 64 : 1,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">{customSuffix}</InputAdornment>
            ),
          }}
          sx={{ mt: 1, maxWidth: 160 }}
        />
      )}

      {!isCustom && activeTier && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
          {activeTier.value}
        </Typography>
      )}

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}

export default ResourceTierButtons;
